import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import logoMark from '../../assets/LogoIcon.svg';
import { cn } from '../../lib/cn';
import { HudPath, hudPaths, toHudHash } from '../content';
import { StatusChip } from './StatusChip';
import { SystemButton } from './SystemButton';

type CommandRoute = {
  id: string;
  path: HudPath;
  label: string;
  shortLabel: string;
};

export const CommandNav = ({
  activePath,
  routes,
  terminalPath
}: {
  activePath: HudPath;
  routes: readonly CommandRoute[];
  terminalPath: HudPath;
}) => {
  const reducedMotion = useReducedMotion();
  const homeActive = activePath === hudPaths.home;
  const activeIndex = Math.max(0, routes.findIndex((module) => module.path === activePath));
  const routeProgress = (activeIndex + 1) / routes.length;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-brand-lime/25 bg-[linear-gradient(180deg,rgba(2,7,10,0.95),rgba(2,8,11,0.9))] backdrop-blur-xl">
        <motion.div
          className="pointer-events-none absolute inset-y-0 left-[-22%] w-[20%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.22),transparent)]"
          animate={reducedMotion ? undefined : { x: ['0%', '650%'], opacity: [0, 0.7, 0] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }}
        />

        <div className="hud-shell flex min-h-[4.3rem] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
          <a href={toHudHash(hudPaths.home)} className="flex items-center gap-2.5">
            <div className="relative grid h-8 w-8 place-items-center overflow-hidden rounded-md border border-brand-lime/45 bg-brand-panel-2/80 p-1">
              <img src={logoMark} alt="DeveLogic Digital logo" className="h-full w-full object-contain" />
              <motion.span
                className="pointer-events-none absolute inset-0 border border-brand-lime/35"
                animate={reducedMotion ? undefined : { opacity: [0.2, 0.65, 0.2] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
            <span className="font-display text-sm tracking-[0.16em] text-brand-text sm:text-base">DEVELOGIC // OS</span>
          </a>

          <nav aria-label="Command navigation" className={cn('hidden xl:block', homeActive && 'xl:hidden')}>
            <ul className="flex items-center gap-2">
              {routes.map((route) => {
                const active = route.path === activePath;

                return (
                  <li key={route.id}>
                    <a
                      href={toHudHash(route.path)}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'hud-nav-link whitespace-nowrap',
                        active &&
                          'border-brand-lime/60 bg-[linear-gradient(180deg,rgba(183,255,106,0.2),rgba(183,255,106,0.06))] text-brand-text'
                      )}
                    >
                      {route.shortLabel}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="flex items-center gap-2">
            <StatusChip label="SYSTEM ONLINE" className="hidden sm:inline-flex" />
            <SystemButton href={toHudHash(terminalPath)} variant="secondary" className="px-3 py-2 text-[0.6rem]">
              Open Terminal
            </SystemButton>
          </div>
        </div>

        <div className="h-[2px] w-full bg-brand-lime/20">
          <motion.div
            className="h-full origin-left bg-gradient-to-r from-brand-lime via-brand-aqua to-brand-lime"
            animate={{ scaleX: routeProgress }}
            transition={{ duration: 0.4, ease: [0.2, 1, 0.32, 1] }}
          />
        </div>
      </header>

      {homeActive ? null : (
        <nav className="fixed inset-x-0 bottom-2 z-50 px-2 xl:hidden" aria-label="Command navigation mobile">
          <div className="mx-auto flex w-full max-w-[40rem] items-center gap-1 overflow-x-auto rounded-xl border border-brand-lime/22 bg-brand-panel/85 p-1 backdrop-blur-xl">
            {routes.map((route) => {
              const active = route.path === activePath;
              return (
                <a
                  key={route.id}
                  href={toHudHash(route.path)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'min-w-[5.2rem] flex-1 rounded-lg px-2 py-2 text-center font-mono text-[0.58rem] uppercase tracking-[0.18em] text-brand-text-2',
                    active
                      ? 'border border-brand-lime/60 bg-brand-lime/14 text-brand-text'
                      : 'border border-transparent hover:border-brand-lime/30'
                  )}
                >
                  {route.shortLabel}
                </a>
              );
            })}
          </div>
        </nav>
      )}
    </>
  );
};
